import { useEffect, useState } from 'react';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { NotificationManager } from 'react-notifications';
import SentryScopedError from 'Errors/SentryScopedError';
import fr from 'facet-rest';
import { INVALID_LINK_TOKEN } from 'Components/UI/PlaidComponent';
import { dispatchPlaidExit, dispatchPlaidStart } from 'Hooks/useRefreshEvent';


class PlaidComponentError extends SentryScopedError {
  constructor(message, sentryScope, ...params) {
    super(sentryScope, ...params);

    this.name = 'PlaidComponentError';
    this.message = message;
  }
}

export default function usePlaidLink({
  onSuccess, onFailure, onExit, onClick, plaidTokenLink, onLoad,
}) {
  const { householdResource } = useSimpleHousehold();
  const [tokenResource, setTokenResource] = useState(null);

  const loadLinkToken = async () => {
    if (!plaidTokenLink) return;
    const [plaidTokenResource] = await fr.http.get(plaidTokenLink);
    setTokenResource(plaidTokenResource);
  };

  useEffect(() => {
    loadLinkToken();
  }, [householdResource, plaidTokenLink]);

  const getAccountIds = (accounts) => {
    const ids = [];
    accounts.forEach((account) => {
      ids.push(account.id);
    });

    return ids;
  };

  const plaidSuccess = async (publicToken, metadata) => {
    const tokenResourceCopy = tokenResource.copy();
    tokenResourceCopy.attr('token', publicToken);
    if (metadata.account && metadata.account.verification_status !== 'manually_verified') {
      const accountIds = getAccountIds(metadata.accounts);
      tokenResource.attr('accountId', accountIds);
    }
    try {
      const exchangeTokenResp = await tokenResourceCopy.submit();
      const isPending = (metadata.account.verification_status === 'pending_automatic_verification' || metadata.account.verification_status === 'pending_manual_verification');
      if (onSuccess) {
        onSuccess(exchangeTokenResp, isPending);
      }
    } catch (e) {
      if (e.toString().includes('Timeout of 60000ms exceeded')) {
        NotificationManager.warning(
          `We are still working on syncing your ${metadata.institution.name} accounts.  Please check back in a few minutes`,
        );
      } else if (onFailure) {
        onFailure(e);
      }
    } finally {
      dispatchPlaidExit();
    }
  };

  const handlePlaidEvent = async (eventName, metadata) => {
    if (eventName !== 'TRANSITION_VIEW' && eventName !== 'SEARCH_INSTITUTION') { // These are useless events, it just says what screen they are on.
      // Log the event to our plaidEvent table, wrap in a try catch so we do not blow up on fail. This is a background process
      try {
        householdResource.relationship('plaidEvent').link('related').post({ attributes: { eventName, ...metadata, linkType: tokenResource.attr('linkType') } }, { deprecatedAllowAlterOfRelation: true });
      } catch (e) {
        // eslint-disable-next-line
        console.log('error logging plaid event', e);
      }
    }
  };

  const handleOnExit = async (err, metadata) => {
    if (onExit) {
      onExit(err);
    }

    if (err != null && err.error_code === INVALID_LINK_TOKEN) {
      window.Plaid.destroy();
      await loadLinkToken();
    }

    // On cancel this is an empty error object, so check if type set before showing snackbar.
    if (err && err.type) {
      const { error_message, ...rest } = err; // eslint-disable-line camelcase
      displayErrorMessage(
        new PlaidComponentError(error_message, {
          ...rest,
          ...metadata,
        }),
        'Failed to sync account',
      );
    }
    dispatchPlaidExit();
  };

  function handleOnLoad() {
    if (onLoad) {
      onLoad();
    }
  }

  async function handleClick(event) {
    const options = {
      token: tokenResource.attr('token'),
      onEvent: handlePlaidEvent,
      onExit: handleOnExit,
      onLoad: handleOnLoad,
      onSuccess: plaidSuccess,
    };

    if (event) event.stopPropagation();
    if (onClick) {
      onClick();
    }
    window.Plaid.create(options).open();
    dispatchPlaidStart();
  }

  return {
    handleClick,
    token: tokenResource?.attr('token'),
    disabled: !tokenResource,
  };
}
